import { gql } from '@apollo/client';

export const GET_PROPERTIES = gql`
  query GetProperties($limit: Int!, $page: Int!, $search: String, $startDate: String!, $endDate: String!) {
    propertiesWithReservations(
      input: { 
        limit: $limit, 
        page: $page, 
        search: $search,
        startDate: $startDate,
        endDate: $endDate
      }
    ) {
      properties {
        id
        name
        address
        city
        state
        zip
        area
        propertyCode
        status
        isActive
        lastSynced
        integratedWithStreamline
        price
        payout
      }
      total
      page
      limit
    }
  }
`;

// Simple query to get all properties without date filtering (for filters/dropdowns)
export const GET_ALL_PROPERTIES = gql`
  query GetAllProperties($limit: Int) {
    allProperties(limit: $limit) {
      items {
        id
        name
        address
        city
        state
        zip
        area
        propertyCode
        status
        isActive
      }
      total
    }
  }
`;

export const GET_GUEST_ALERT_RESERVATION_CANDIDATE = gql`
  query GetGuestAlertReservationCandidate($propertyId: ID!) {
    guestAlertReservationCandidate(propertyId: $propertyId) {
      available
      reason
      reservationId
      guestName
      maskedEmail
      maskedPhone
      hasEmail
      hasPhone
      checkoutAt
    }
  }
`;

export const UPDATE_PROPERTY = gql`
  mutation UpdateProperty($id: ID!, $input: UpdatePropertyInput!) {
    updateProperty(id: $id, updatePropertyInput: $input) {
      id
      streamlinePropertyId
      name
      address
      city
      state
      zip
      area
      propertyCode
      status
      isActive
      lastSynced
      checkInTime
      checkOutTime
      entryDetails
      propertyManagerId
      propertyManagerIds
      propertyManagementTeamId
      propertyManagementTeam {
        id
        name
        color
        department
      }
      beds
      baths
      housekeepingNotes
      stagingNotes
      stagingPhotos
      propertyManagerNotes
      websiteLink
      price
      ownerPrice
      payout
      externalId
      integratedWithStreamline
      cleaningAssignedUserId
      inspectionAssignedUserId
      walkthroughAssignedUserId
      walkthroughJobCreationEnabled
      quickbooksCustomerId
      hvacFiltersLastChanged
      instructionalVideos {
        id
        title
        description
        videoUrl
        sortOrder
        createdAt
        updatedAt
      }
      created_at
      updated_at
    }
  }
`;

export const SAVE_PROPERTY_INSTRUCTIONAL_VIDEOS = gql`
  mutation UpdatePropertyInstructionalVideos($propertyId: ID!, $videos: [InstructionalVideoInput!]!) {
    updatePropertyInstructionalVideos(propertyId: $propertyId, videos: $videos) {
      id
      instructionalVideos {
        id
        title
        description
        videoUrl
        sortOrder
      }
    }
  }
`;

export const UPDATE_PROPERTY_HVAC_FILTERS_LAST_CHANGED = gql`
  mutation UpdatePropertyHvacFiltersLastChanged($id: ID!, $hvacFiltersLastChanged: String) {
    updatePropertyHvacFiltersLastChanged(id: $id, hvacFiltersLastChanged: $hvacFiltersLastChanged) {
      id
      hvacFiltersLastChanged
    }
  }
`;

export const UPDATE_PROPERTY_HVAC_FILTERS = gql`
  mutation UpdatePropertyHvacFilters($id: ID!, $hvacFilters: [HvacFilterInput!], $hvacFiltersLastChanged: String) {
    updatePropertyHvacFilters(id: $id, hvacFilters: $hvacFilters, hvacFiltersLastChanged: $hvacFiltersLastChanged) {
      id
      hvacFilters {
        id
        filterLocation
        filterSize
      }
      hvacFiltersLastChanged
    }
  }
`;

export const ARCHIVE_PROPERTY = gql`
  mutation ArchiveProperty($id: ID!) {
    archiveProperty(id: $id)
  }
`;

export const RESTORE_PROPERTY = gql`
  mutation RestoreProperty($id: ID!) {
    restoreProperty(id: $id)
  }
`;

export const GET_CANCELLED_JOBS_COUNT = gql`
  query GetCancelledJobsCount($propertyId: ID!, $sinceDate: Timestamp) {
    getCancelledJobsCount(propertyId: $propertyId, sinceDate: $sinceDate)
  }
`;

export const GET_FUTURE_JOBS_COUNT = gql`
  query GetFutureJobsCount($propertyId: ID!, $archiveDate: Timestamp) {
    getFutureJobsCount(propertyId: $propertyId, archiveDate: $archiveDate)
  }
`;

export const DELETE_FUTURE_WALKTHROUGH_JOBS = gql`
  mutation DeleteFutureWalkthroughJobs($propertyId: ID!) {
    deleteFutureWalkthroughJobs(propertyId: $propertyId)
  }
`;

export const DELETE_PROPERTY = gql`
  mutation DeleteProperty($id: ID!) {
    removeProperty(id: $id)
  }
`;

export const PROPERTY_HAS_RELATIONS = gql`
  query PropertyHasRelations($id: ID!) {
    propertyHasRelations(id: $id)
  }
`;

export const GET_RESERVATIONS = gql`
  query GetReservations {
    reservations {
      id
      streamline_reservation_id
      check_in
      check_out
      check_in_time
      check_out_time
      has_time_override
      override_check_in
      override_check_out
      guest_name
      email
      mobile_phone
      is_cancelled
      type_name
      isOwnerBlock
      isMaintenanceBlock
      isNonPayingGuest
      isPayingGuest
      property {
        id
        name
        address
      }
      created_at
      updated_at
    }
  }
`;

export const GET_PROPERTY_RESERVATIONS = gql`
  query GetPropertyReservations($propertyId: ID!) {
    propertyReservations(propertyId: $propertyId) {
      id
      streamline_reservation_id
      check_in
      check_out
      check_in_time
      check_out_time
      has_time_override
      override_check_in
      override_check_out
      guest_name
      email
      mobile_phone
      is_cancelled
      type_name
      isOwnerBlock
      isMaintenanceBlock
      isNonPayingGuest
      isPayingGuest
      property {
        id
        name
        address
        checkInTime
        checkOutTime
      }
      created_at
      updated_at
    }
  }
`;

export const GET_UPCOMING_RESERVATIONS = gql`
  query GetUpcomingReservations($days: Float) {
    upcomingReservations(days: $days) {
      id
      streamline_reservation_id
      check_in
      check_out
      check_in_time
      check_out_time
      has_time_override
      override_check_in
      override_check_out
      guest_name
      email
      mobile_phone
      is_cancelled
      type_name
      isOwnerBlock
      isMaintenanceBlock
      isNonPayingGuest
      isPayingGuest
      property {
        id
        name
        address
      }
      created_at
      updated_at
    }
  }
`;

export const GET_OVERLAPPING_CHECKIN_CHECKOUT = gql`
  query GetOverlappingCheckInOut($date: DateTime!) {
    overlappingCheckInOut(date: $date) {
      id
      streamline_reservation_id
      check_in
      check_out
      check_in_time
      check_out_time
      override_check_in
      override_check_out
      has_time_override
      guest_name
      email
      mobile_phone
      is_cancelled
      type_name
      isOwnerBlock
      isMaintenanceBlock
      isNonPayingGuest
      isPayingGuest
      property {
        id
        name
        address
        area
      }
      created_at
      updated_at
    }
  }
`;

export const GET_PROPERTIES_WITH_RESERVATIONS = gql`
  query GetPropertiesWithReservations($input: PropertyWithReservationsInput!) {
    propertiesWithReservations(input: $input) {
      properties {
        id
        name
        address
        city
        state
        zip
        area
        propertyCode
        isActive
        reservations {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          override_check_in
          override_check_out
          has_time_override
          email
          mobile_phone
          is_cancelled
          status_code
          status_id
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
          property {
            id
            name
            address
            area
          }
        }
      }
      total
      page
      limit
    }
  }
`;

export const GET_PROPERTIES_WITH_ACTIVE_RESERVATIONS = gql`
  query GetPropertiesWithActiveReservations($input: PropertyWithReservationsInput!) {
    propertiesWithActiveReservations(input: $input) {
      properties {
        id
        name
        address
        city
        state
        zip
        area
        propertyCode
        reservations {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          override_check_in
          override_check_out
          has_time_override
          email
          mobile_phone
          is_cancelled
          status_code
          status_id
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
          property {
            id
            name
            address
            area
          }
        }
      }
      total
      page
      limit
    }
  }
`;

export const GET_STREAMLINE_STATUS = gql`
  query GetStreamlineStatus {
    getStreamlineStatus {
      isConnected
      lastCheck
      message
    }
  }
`;

export const GET_USERS = gql`
  query GetUsers {
    users {
      id
      firstName
      lastName
      email
      phoneNumber
      avatarUrl
      role
      isActive
      locationPreference
      areas
      name
      teamId
      isContractor
      salary
      paidTimeOffDays
      requiresTimeClock
      timeClockHourlyRate
      showAllDepartmentsByDefault
      receivesHousekeepingNotifications
      team {
        id
        name
        description
        color
        isActive
      }
      userTeams {
        id
        userId
        teamId
        isPrimary
        createdAt
        team {
          id
          name
          color
        }
      }
    }
  }
`;

// Avatar values are now short S3 keys after the legacy base64 migration, so it
// is safe for the directory to request and resolve them again.
export const GET_TEAM_MEMBER_DIRECTORY_USERS = gql`
  query GetTeamMemberDirectoryUsers {
    users {
      id
      firstName
      lastName
      email
      phoneNumber
      avatarUrl
      role
      isActive
      locationPreference
      areas
      name
      teamId
      isContractor
      salary
      paidTimeOffDays
      requiresTimeClock
      timeClockHourlyRate
      showAllDepartmentsByDefault
      receivesHousekeepingNotifications
      emailNotifications
      smsNotifications
      notificationEmailOverride
      team {
        id
        name
        description
        color
        isActive
      }
      userTeams {
        id
        userId
        teamId
        isPrimary
        createdAt
        team {
          id
          name
          color
        }
      }
    }
  }
`;

// Optimized version for assignment dropdowns - only essential fields
export const GET_USERS_MINIMAL = gql`
  query GetUsersMinimal {
    usersMinimal {
      id
      firstName
      lastName
      name
      role
      isActive
      teamId
      team {
        id
        name
        color
      }
    }
  }
`;

export const SEARCH_RESERVATIONS = gql`
  query SearchReservations($searchTerm: String!) {
    searchReservations(searchTerm: $searchTerm) {
      id
      guest_name
      check_in
      check_out
      check_in_time
      check_out_time
      override_check_in
      override_check_out
      has_time_override
      email
      mobile_phone
      is_cancelled
      status_code
      status_id
      type_name
      isOwnerBlock
      isMaintenanceBlock
      isNonPayingGuest
      isPayingGuest
      property {
        id
        name
        address
        city
        state
        zip
        area
      }
    }
  }
`;

export const GET_RESERVATIONS_BY_DATE = gql`
  query GetReservationsByDate($input: ReservationsByDateInput!) {
    reservationsByDate(input: $input) {
      reservations {
        id
        guest_name
        check_in
        check_out
        check_in_time
        check_out_time
        override_check_in
        override_check_out
        has_time_override
        email
        mobile_phone
        is_cancelled
        status_code
        status_id
        type_name
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
        property {
          id
          name
          address
          city
          state
          zip
          area
        }
      }
      total
      page
      limit
    }
  }
`;

export const GET_JOBS = gql`
  query GetJobs($input: JobsQueryInput!) {
    jobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isFlip
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
          payout
          price
          propertyManagementTeam {
            id
            name
          }
          propertyManagers {
            id
            firstName
            lastName
          }
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
          role
        }
        assignedTeamId
        assignedTeam {
          id
          name
          color
          memberCount
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        customFlags {
          id
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          templateId
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isBuiltIn
            isActive
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
        count
      }
    }
  }
`;

export const GET_CANCELED_JOBS = gql`
  query GetCanceledJobs($input: JobsQueryInput!) {
    scheduleCanceledJobsOptimized(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isFlip
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
          payout
          price
          propertyManagementTeam {
            id
            name
          }
          propertyManagers {
            id
            firstName
            lastName
          }
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
          role
        }
        assignedTeamId
        assignedTeam {
          id
          name
          color
          memberCount
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        customFlags {
          id
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          templateId
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isBuiltIn
            isActive
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
        count
      }
    }
  }
`;

/**
 * 🔒 SECURE: GraphQL query for MySchedule page
 * Uses the secure myScheduleJobs endpoint that only returns directly assigned jobs
 */
export const GET_MY_SCHEDULE_JOBS = gql`
  query GetMyScheduleJobs($input: JobsQueryInput!) {
    myScheduleJobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isFlip
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
          payout
          price
          propertyManagementTeam {
            id
            name
          }
          propertyManagers {
            id
            firstName
            lastName
          }
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
          role
        }
        assignedTeamId
        assignedTeam {
          id
          name
          color
          memberCount
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        customFlags {
          id
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          templateId
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isBuiltIn
            isActive
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
        count
      }
    }
  }
`;

export const GET_JOB = gql`
  query GetJob($id: ID!) {
    job(id: $id) {
      id
      category
      status
      cleaningType
      notes
      scheduledDate
      startTime
      hasDateOverride
      hasTimeOverride
      isOwnerArrival
      isOwnerDeparture
      isMaintenanceStart
      isMaintenanceEnding
      isEarlyCheckin
      isFlip
      payout
      billedAmount
      property {
        id
        name
        address
        city
        state
        zip
        propertyCode
        checkInTime
        checkOutTime
        entryDetails
        beds
        baths
        propertyManagerId
        propertyManagerIds
        propertyManagementTeamId
        propertyManagementTeam {
          id
          name
          color
          department
          members {
            id
            firstName
            lastName
            phoneNumber
            name
          }
          userTeams {
            user {
              id
              firstName
              lastName
              phoneNumber
              name
            }
          }
        }
        propertyManager {
          id
          firstName
          lastName
          phoneNumber
          name
        }
        propertyManagers {
          id
          firstName
          lastName
          phoneNumber
          name
        }
        payout
        price
        latitude
        longitude
        stagingPhotos
        housekeepingNotes
        stagingNotes
        propertyManagerNotes
      }
      reservation {
        id
        guest_name
        email
        mobile_phone
        check_in
        check_out
        check_in_time
        check_out_time
        override_check_in
        override_check_out
        has_time_override
        type_name
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
      }
      previousReservation {
        id
        guest_name
        email
        mobile_phone
        check_in
        check_out
        check_in_time
        check_out_time
        has_time_override
        override_check_in
        override_check_out
        type_name
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
      }
      serviceType {
        id
        title
        color
        timeBase
        defaultTime
        requiresChecklist
        requiresCheckIn
        canSubmitChecklist
        useChecklistStyle
        showFinancialFields
        usePropertyFinancialFields
        checklist {
          id
          title
          items {
            id
            title
            description
            categoryId
            isRequired
            sortOrder
            category {
              id
              title
            }
          }
        }
      }
      assignedTo {
        id
        name
        role
        phoneNumber
      }
      assignedTeamId
      assignedTeam {
        id
        name
        color
        memberCount
      }
      statusHistory {
        id
        status
        timestamp
        notes
        latitude
        longitude
        accuracy
        updatedBy {
          id
          name
        }
      }
      customFlags {
        id
        flagType
        flagValue
        flagColor
        isManual
        addedByUserId
        sortOrder
        templateId
        template {
          id
          flagValue
          iconName
          backgroundColor
          textColor
          isBuiltIn
          isActive
        }
        createdAt
        updatedAt
      }
    }
  }
`;

// Lightweight query for EditJobModal - excludes stagingPhotos to improve performance
export const GET_JOB_FOR_EDIT = gql`
  query GetJobForEdit($id: ID!) {
    job(id: $id) {
      id
      category
      status
      cleaningType
      notes
      scheduledDate
      startTime
      hasDateOverride
      hasTimeOverride
      isOwnerArrival
      isOwnerDeparture
      isMaintenanceStart
      isMaintenanceEnding
      isEarlyCheckin
      isFlip
      payout
      billedAmount
      property {
        id
        name
        address
        city
        state
        zip
        propertyCode
        checkInTime
        checkOutTime
        entryDetails
        beds
        baths
        propertyManagerId
        payout
        price
      }
      reservation {
        id
        guest_name
        email
        mobile_phone
        check_in
        check_out
        check_in_time
        check_out_time
        override_check_in
        override_check_out
        has_time_override
        type_name
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
      }
      previousReservation {
        id
        guest_name
        email
        mobile_phone
        check_in
        check_out
        check_in_time
        check_out_time
        has_time_override
        override_check_in
        override_check_out
        type_name
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
      }
      serviceType {
        id
        title
        category
        color
        timeBase
        defaultTime
        requiresChecklist
        requiresCheckIn
        canSubmitChecklist
        showFinancialFields
        usePropertyFinancialFields
        checklist {
          id
          title
          items {
            id
            title
            description
            categoryId
            isRequired
            category {
              id
              title
            }
          }
        }
      }
      assignedTo {
        id
        name
        role
        phoneNumber
      }
      assignedTeamId
      assignedTeam {
        id
        name
        color
        memberCount
      }
      statusHistory {
        id
        status
        timestamp
        notes
        updatedBy {
          id
          name
        }
      }
      customFlags {
        id
        flagType
        flagValue
        flagColor
        isManual
        addedByUserId
        sortOrder
        createdAt
        updatedAt
      }
    }
  }
`;

export const ASSIGN_JOBS = gql`
  mutation AssignJobs($jobIds: [ID!]!, $userId: ID!) {
    assignJobs(jobIds: $jobIds, userId: $userId) {
      id
      status
      category
      scheduledDate
      notes
      property {
        id
        name
        address
        propertyCode
      }
      reservation {
        id
        guest_name
        check_in
        check_out
        check_in_time
        check_out_time
        override_check_in
        override_check_out
        has_time_override
      }
      assignedTo {
        id
        name
        role
        phoneNumber
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_PROPERTY_DETAILS = gql`
  query GetPropertyDetails($id: ID!) {
    property(id: $id) {
      id
      streamlinePropertyId
      name
      address
      city
      state
      zip
      area
      propertyCode
      status
      isActive
      lastSynced
      checkInTime
      checkOutTime
      entryDetails
      propertyManagerId
      propertyManagerIds
      propertyManagementTeamId
      propertyManagementTeam {
        id
        name
        color
        department
        members {
          id
          firstName
          lastName
          phoneNumber
          name
        }
        userTeams {
          user {
            id
            firstName
            lastName
            phoneNumber
            name
          }
        }
      }
      propertyManager {
        id
        firstName
        lastName
        phoneNumber
      }
      propertyManagers {
        id
        firstName
        lastName
        name
        phoneNumber
      }
      beds
      baths
      housekeepingNotes
      stagingNotes
      stagingPhotos
      propertyManagerNotes
      websiteLink
      price
      ownerPrice
      payout
      externalId
      integratedWithStreamline
      cleaningAssignedUserId
      inspectionAssignedUserId
      walkthroughAssignedUserId
      walkthroughJobCreationEnabled
      quickbooksCustomerId
      hvacFilters {
        id
        filterLocation
        filterSize
        createdAt
        updatedAt
      }
      hvacFiltersLastChanged
      instructionalVideos {
        id
        title
        description
        videoUrl
        sortOrder
        createdAt
        updatedAt
      }
      created_at
      updated_at
    }
  }
`;

export const GET_PROPERTY_MANAGERS = gql`
  query GetPropertyManagers {
    propertyManagers {
      id
      name
      email
      role
    }
  }
`;

export const GET_SERVICE_TYPES = gql`
  query GetServiceTypes {
    serviceTypes {
      id
      title
      category
      isActive
      allowedRoles
      color
      showFinancialFields
      usePropertyFinancialFields
      departments
    }
  }
`;

export const GET_PUBLIC_JOB = gql`
  query GetPublicJob($id: ID!, $token: String) {
    publicJob(id: $id, token: $token) {
      id
      category
      status
      cleaningType
      notes
      scheduledDate
      isOwnerArrival
      isOwnerDeparture
      isMaintenanceStart
      isMaintenanceEnding
      isEarlyCheckin
      property {
        id
        name
        address
        city
        state
        zip
        propertyCode
        checkInTime
        checkOutTime
        entryDetails
        beds
        baths
        propertyManagerId
        propertyManagerIds
        propertyManagementTeamId
        propertyManagementTeam {
          id
          name
          color
          department
          members {
            id
            firstName
            lastName
            phoneNumber
            name
          }
          userTeams {
            user {
              id
              firstName
              lastName
              phoneNumber
              name
            }
          }
        }
        propertyManager {
          id
          firstName
          lastName
          phoneNumber
          name
        }
        propertyManagers {
          id
          firstName
          lastName
          phoneNumber
          name
        }
        payout
        latitude
        longitude
        stagingPhotos
        housekeepingNotes
        stagingNotes
        propertyManagerNotes
      }
      reservation {
        id
        guest_name
        email
        mobile_phone
        check_in
        check_out
        check_in_time
        check_out_time
        override_check_in
        override_check_out
        has_time_override
        type_name
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
      }
      serviceType {
        id
        title
        color
        timeBase
        defaultTime
        requiresChecklist
        requiresCheckIn
        canSubmitChecklist
        showFinancialFields
        usePropertyFinancialFields
        checklist {
          id
          title
          items {
            id
            title
            description
            categoryId
            isRequired
            category {
              id
              title
            }
          }
        }
      }
      assignedTo {
        id
        name
        role
        phoneNumber
      }
      statusHistory {
        id
        status
        timestamp
        notes
        latitude
        longitude
        accuracy
        updatedBy {
          id
          name
        }
      }
      customFlags {
        id
        flagType
        flagValue
        flagColor
        isManual
        addedByUserId
        sortOrder
        templateId
        template {
          id
          flagValue
          iconName
          backgroundColor
          textColor
          isBuiltIn
          isActive
        }
        createdAt
        updatedAt
      }
    }
  }
`;

export const SYNC_WORK_ORDERS = gql`
  mutation SyncStreamlineWorkOrders($input: SyncWorkOrdersDto!) {
    syncStreamlineWorkOrders(input: $input) {
      successCount
      failureCount
      skippedCount
      timestamp
    }
  }
`;

export const GET_WORK_ORDERS = gql`
  query GetWorkOrders($input: WorkOrdersQueryInput) {
    workOrders(input: $input) {
      items {
        id
        description
        isUrgent
        isAfterHours
        isFeedback
        workOrderType
        status
        hasActiveTimer
        createdAt
        updatedAt
        photos
        streamlineWorkOrderId
        submitterName
        isGuestSubmitted
        guestName
        guestPhone
        guestAlertsEnabled
        guestAlertDescription
        guestAlertRecipientName
        guestAlertContactSource
        guestAssignmentAlertProcessed
        guestAlertsDisabledAt
        needsApproval
        approvalReason
        approvalRequestedAt
        approvalApproved
        approvalApprovedAt
        approvedBy {
          id
          name
        }
        assignedToId
        assignedRoles
        assignedTeamId
        assignedTeam {
          id
          name
          color
        }
        assignedTo {
          id
          name
          role
        }
        dueDate
        commentCount
        urgentCommentCount
        lastCommentAt
        customFlags {
          id
          workOrderId
          templateId
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          createdAt
          updatedAt
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isActive
          }
        }
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
        }
        reservation {
          id
          guest_name
          check_in
          check_out
        }
        isDeleted
        deletedAt
        deletedBy {
          id
          firstName
          lastName
        }
        submittedBy {
          id
          name
        }
      }
      total
      page
      limit
    }
  }
`;

export const GET_WORK_ORDER_FEED = gql`
  query GetWorkOrderFeed($input: WorkOrderFeedInput) {
    workOrderFeed(input: $input) {
      items {
        id
        description
        isUrgent
        isAfterHours
        isFeedback
        workOrderType
        status
        hasActiveTimer
        createdAt
        updatedAt
        photos
        streamlineWorkOrderId
        submitterName
        isGuestSubmitted
        guestName
        guestPhone
        guestAlertsEnabled
        guestAlertDescription
        guestAlertRecipientName
        guestAlertContactSource
        guestAssignmentAlertProcessed
        guestAlertsDisabledAt
        needsApproval
        approvalReason
        approvalRequestedAt
        approvalApproved
        approvalApprovedAt
        approvedBy {
          id
          name
        }
        assignedToId
        assignedRoles
        assignedTeamId
        assignedTeam {
          id
          name
          color
        }
        assignedTo {
          id
          name
          role
        }
        dueDate
        commentCount
        urgentCommentCount
        lastCommentAt
        customFlags {
          id
          workOrderId
          templateId
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          createdAt
          updatedAt
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isActive
          }
        }
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
        }
        reservation {
          id
          guest_name
          check_in
          check_out
        }
        submittedBy {
          id
          name
        }
      }
      total
      page
      limit
      pastCount
      newCount
      dueTodayCount
    }
  }
`;

export const GET_WORK_ORDER = gql`
  query GetWorkOrder($id: ID!) {
    workOrder(id: $id) {
      id
      description
      isUrgent
      isAfterHours
      isFeedback
      workOrderType
      status
      hasActiveTimer
      createdAt
      updatedAt
      completedAt
      photos
      videos {
        id
        url
        thumbnailUrl
        duration
        sizeBytes
        createdAt
        expiresAt
      }
      streamlineWorkOrderId
      submitterName
      isGuestSubmitted
      guestName
      guestPhone
      guestAlertsEnabled
      guestAlertDescription
      guestAlertRecipientName
      guestAlertContactSource
      guestAssignmentAlertProcessed
      guestAlertsDisabledAt
      reservationId
      needsApproval
      approvalReason
      approvalRequestedAt
      approvalApproved
      approvalApprovedAt
      approvedBy {
        id
        name
      }
      assignedToId
      assignedRoles
      assignedTeamId
      assignedTeam {
        id
        name
        color
      }
      assignedTo {
        id
        name
        role
      }
      dueDate
      commentCount
      urgentCommentCount
      lastCommentAt
      customFlags {
        id
        workOrderId
        templateId
        flagType
        flagValue
        flagColor
        isManual
        addedByUserId
        sortOrder
        createdAt
        updatedAt
        template {
          id
          flagValue
          iconName
          backgroundColor
          textColor
          isActive
        }
      }
      property {
        id
        name
        address
        city
        state
        zip
        area
        propertyCode
        checkInTime
        checkOutTime
        entryDetails
        propertyManagerId
        propertyManagementTeamId
        latitude
        longitude
        propertyManagementTeam {
          id
          name
          color
          department
          members {
            id
            firstName
            lastName
            phoneNumber
            name
          }
          userTeams {
            user {
              id
              firstName
              lastName
              phoneNumber
              name
            }
          }
        }
        propertyManager {
          id
          firstName
          lastName
          phoneNumber
        }
        propertyManagers {
          id
          firstName
          lastName
          phoneNumber
        }
      }
      reservation {
        id
        guest_name
        check_in
        check_out
      }
      isDeleted
      deletedAt
      deletedBy {
        id
        name
        firstName
        lastName
      }
      submittedBy {
        id
        name
        firstName
        lastName
        email
        role
      }
    }
  }
`;

export const GET_WORK_ORDER_COMMENTS = gql`
  query GetWorkOrderComments($workOrderId: ID!, $limit: Int, $offset: Int) {
    workOrderComments(input: { workOrderId: $workOrderId, limit: $limit, offset: $offset }) {
      comments {
        id
        workOrderId
        userId
        comment
        isEscalation
        attachments
        createdAt
        updatedAt
        editedAt
        user {
          id
          name
          firstName
          lastName
          role
          avatarUrl
        }
      }
      total
      hasMore
    }
  }
`;

export const GET_PUBLIC_PROPERTY_RESERVATIONS = gql`
  query GetPublicPropertyReservations($propertyId: ID!, $jobId: ID!, $token: String!) {
    publicPropertyReservations(propertyId: $propertyId, jobId: $jobId, token: $token) {
      id
      streamline_reservation_id
      check_in
      check_out
      check_in_time
      check_out_time
      has_time_override
      override_check_in
      override_check_out
      guest_name
      is_cancelled
      type_name
      isOwnerBlock
      isMaintenanceBlock
      isNonPayingGuest
      isPayingGuest
      property {
        id
        name
        address
        checkInTime
        checkOutTime
      }
      created_at
      updated_at
    }
  }
`;

export const GET_NEXT_RESERVATION = gql`
  query GetNextReservation($propertyId: String!, $date: String) {
    nextReservationForProperty(propertyId: $propertyId, date: $date) {
      id
      check_in
      check_out
      check_in_time
      check_out_time
      has_time_override
      override_check_in
      override_check_out
      guest_name
      email
      mobile_phone
      is_cancelled
      status_code
      type_name
      isOwnerBlock
      property {
        id
        name
        checkInTime
        checkOutTime
      }
    }
  }
`;

export const GET_PREVIOUS_RESERVATION = gql`
  query GetPreviousReservation($propertyId: String!, $date: String) {
    previousReservationForProperty(propertyId: $propertyId, date: $date) {
      id
      check_in
      check_out
      check_in_time
      check_out_time
      has_time_override
      override_check_in
      override_check_out
      guest_name
      email
      mobile_phone
      is_cancelled
      status_code
      type_name
      isOwnerBlock
      property {
        id
        name
        checkInTime
        checkOutTime
      }
    }
  }
`;

export const HAS_LATE_CHECKOUT_FOR_CUSTOM_JOB = gql`
  query HasLateCheckoutForCustomJob($propertyId: String!, $jobDate: String!) {
    hasLateCheckoutForCustomJob(propertyId: $propertyId, jobDate: $jobDate)
  }
`;

export const DEBUG_SERVICE_TYPE_BY_TITLE = gql`
  query DebugServiceTypeByTitle($title: String!) {
    serviceTypeByTitle(title: $title) {
      id
      title
      timeBase
      color
      defaultTime
      requiresChecklist
      requiresCheckIn
      canSubmitChecklist
      isActive
      isBuiltIn
      checklistId
      checklist {
        id
        title
        items {
          id
          title
          categoryId
          isRequired
          category {
            id
            title
          }
        }
      }
    }
  }
`;

export const GET_CREATE_JOB_INPUT_TYPE = gql`
  query IntrospectCreateJobInput {
    __type(name: "CreateJobInput") {
      name
      kind
      inputFields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
  }
`;

export const GET_JOBS_FOR_ASSIGNMENTS = gql`
  query GetJobsForAssignments($input: AssignmentsQueryInput!) {
    jobsForAssignments(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isFlip
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        reservationId
        property {
          id
          name
          address
          propertyCode
          checkInTime
          checkOutTime
          area
          payout
          price
          propertyManager {
            id
            firstName
            lastName
          }
          propertyManagementTeam {
            id
            name
          }
          propertyManagers {
            id
            firstName
            lastName
          }
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
          role
          phoneNumber
        }
        assignedTeamId
        assignedTeam {
          id
          name
          color
          memberCount
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        customFlags {
          id
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          templateId
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isBuiltIn
            isActive
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      assigned
      unassigned
    }
  }
`;

export const GET_ELIGIBLE_USERS_FOR_JOBS = gql`
  query GetEligibleUsersForJobs($input: EligibleUsersQueryInput!) {
    eligibleUsersForJobs(input: $input) {
      id
      name
      email
      role
      phoneNumber
      isActive
      createdAt
      updatedAt
    }
  }
`;

// Recurring Jobs Queries
export const GET_RECURRING_JOBS = gql`
  query GetRecurringJobs {
    recurringJobs {
      id
      name
      category
      propertyId
      property {
        id
        name
        address
        propertyCode
      }
      frequency
      dayOfWeek
      weekOfMonth
      dayOfMonth
      startTime
      notes
      assignedToId
      assignedTo {
        id
        name
        role
      }
      serviceTypeId
      serviceType {
        id
        title
        category
        color
      }
      isActive
      lastGeneratedDate
      createdAt
      updatedAt
      # New computed fields for job counts
      totalJobs
      pendingJobs
      completedJobs
      canceledJobs
    }
  }
`;

export const GET_RECURRING_JOB = gql`
  query GetRecurringJob($id: ID!) {
    recurringJob(id: $id) {
      id
      name
      category
      propertyId
      property {
        id
        name
        address
        propertyCode
      }
      frequency
      dayOfWeek
      weekOfMonth
      dayOfMonth
      startTime
      notes
      assignedToId
      assignedTo {
        id
        name
        role
      }
      serviceTypeId
      serviceType {
        id
        title
        category
        color
      }
      isActive
      lastGeneratedDate
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_RECURRING_JOB = gql`
  mutation CreateRecurringJob($input: CreateRecurringJobInput!) {
    createRecurringJob(input: $input) {
      id
      name
      category
      property {
        id
        name
      }
      frequency
      isActive
    }
  }
`;

export const UPDATE_RECURRING_JOB = gql`
  mutation UpdateRecurringJob($input: UpdateRecurringJobInput!) {
    updateRecurringJob(input: $input) {
      id
      name
      isActive
    }
  }
`;

export const DELETE_RECURRING_JOB = gql`
  mutation DeleteRecurringJob($id: ID!) {
    deleteRecurringJob(id: $id)
  }
`;

export const TOGGLE_RECURRING_JOB_STATUS = gql`
  mutation ToggleRecurringJobStatus($id: ID!, $isActive: Boolean!) {
    toggleRecurringJobStatus(id: $id, isActive: $isActive)
  }
`;

export const GENERATE_RECURRING_JOBS = gql`
  mutation GenerateRecurringJobs {
    generateRecurringJobs
  }
`;

export const CANCEL_RECURRING_JOB_AND_RELATED_JOBS = gql`
  mutation CancelRecurringJobAndRelatedJobs($id: ID!) {
    cancelRecurringJobAndRelatedJobs(id: $id)
  }
`;

export const GET_JOB_CHECKLIST_COMPLETIONS = gql`
  query GetJobChecklistCompletions($jobId: ID!) {
    getJobChecklistCompletions(jobId: $jobId) {
      id
      jobId
      checklistId
      items {
        id
        itemId
        completed
      }
    }
  }
`;

export const GET_ASSOCIATED_CLEANING_JOB = gql`
  query GetAssociatedCleaningJob($inspectionJobId: ID!) {
    associatedCleaningJob(inspectionJobId: $inspectionJobId) {
      id
      status
      category
      propertyId
      scheduledDate
      isOwnerArrival
      isOwnerDeparture
      isMaintenanceStart
      isMaintenanceEnding
      property {
        id
        name
        address
      }
      reservation {
        id
        guest_name
        check_in
        check_out
        isOwnerBlock
        isMaintenanceBlock
        isNonPayingGuest
        isPayingGuest
        type_name
      }
      assignedTo {
        id
        name
        role
      }
      serviceType {
        id
        title
        color
        canSubmitChecklist
        requiresChecklist
        useChecklistStyle
        checklist {
          id
          title
          items {
            id
            title
            category {
              id
              title
            }
            categoryId
            description
            isRequired
            sortOrder
          }
        }
      }
    }
  }
`;

export const GET_PROPERTY_WORK_ORDERS = gql`
  query GetPropertyWorkOrders($propertyId: ID!) {
    workOrders(input: { propertyId: $propertyId, limit: 5 }) {
      items {
        id
        description
        isUrgent
        isAfterHours
        status
        hasActiveTimer
        createdAt
        photos
        property {
          id
          name
        }
        submittedBy {
          id
          name
        }
      }
    }
  }
`;

export const GET_NOTES = gql`
  query GetNotes {
    notes {
      id
      title
      body
      createdAt
      startDate
      endDate
      visibleToRoles
      visibleToAreas
      author {
        id
        name
      }
    }
  }
`;

export const GET_COMPLETED_JOBS = gql`
  query GetCompletedJobs($input: JobsQueryInput!) {
    jobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
        count
      }
    }
  }
`;

export const GET_READY_FOR_INSPECTION_JOBS = gql`
  query GetReadyForInspectionJobs($input: JobsQueryInput!) {
    jobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
      }
    }
  }
`;

// Add a combined query that fetches both READY_FOR_INSPECTION and COMPLETED jobs for the inspections section
export const GET_COMBINED_READY_JOBS = gql`
  query GetCombinedReadyJobs($date: Timestamp!, $categories: [JobCategory!], $statuses: [JobStatus!]!) {
    jobsWithMultipleStatuses(input: { 
      startDate: $date,
      categories: $categories,
      statuses: $statuses,
      limit: 100
    }) {
      items {
        id
        category
        status
        scheduledDate
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        property {
          id
          name
          address
          propertyCode
          checkInTime
          checkOutTime
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          name
          role
        }
      }
      total
    }
  }
`;

export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      firstName
      lastName
      role
      isActive
      isFirstLogin
      phoneNumber
      avatarUrl
      areas
      smsNotifications
      emailNotifications
      notificationEmailOverride
      isContractor
      salary
      paidTimeOffDays
      requiresTimeClock
      timeClockHourlyRate
      lastAuthMethod
    }
  }
`;

export const GET_DELETED_JOBS = gql`
  query GetDeletedJobs($input: JobsQueryInput!) {
    deletedJobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        property {
          id
          name
          address
          city
          state
          zip
          propertyCode
          area
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        createdAt
        updatedAt
      }
      hasMore
      nextCursor
      total
      dateTotals {
        date
        count
      }
    }
  }
`;

// Add this new query for fetching jobs by reservation ID
export const GET_JOBS_BY_RESERVATION_ID = gql`
  query GetJobsByReservationId($reservationId: ID!) {
    jobsByReservation(reservationId: $reservationId) {
      id
      category
      cleaningType
      status
      scheduledDate
      startTime
      property {
        id
        name
      }
      assignedTo {
        id
        name
      }
    }
  }
`;

// New query for assigned employees summary (lightweight)
export const GET_ASSIGNED_EMPLOYEES_SUMMARY = gql`
  query GetAssignedEmployeesSummary($input: AssignmentsQueryInput!) {
    assignedEmployeesSummary(input: $input) {
      employees {
        id
        name
        role
        jobCount
        isTeamAssignment
        teamId
        memberCount
        teamName
        teamColor
      }
      totalJobs
      totalEmployees
    }
  }
`;

// New query for employee-specific assigned jobs (on-demand loading)
export const GET_EMPLOYEE_ASSIGNED_JOBS = gql`
  query GetEmployeeAssignedJobs($input: AssignmentsQueryInput!) {
    employeeAssignedJobs(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isFlip
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        reservationId
        property {
          id
          name
          address
          propertyCode
          checkInTime
          checkOutTime
          area
          propertyManager {
            id
            firstName
            lastName
          }
          propertyManagementTeam {
            id
            name
          }
          propertyManagers {
            id
            firstName
            lastName
          }
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
          role
          phoneNumber
        }
        assignedTeamId
        assignedTeam {
          id
          name
          color
          memberCount
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        customFlags {
          id
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          templateId
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isBuiltIn
            isActive
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      total
      assigned
      hasMore
      nextCursor
    }
  }
`;

// New query specifically for assigned jobs without pagination limits
export const GET_ASSIGNED_JOBS_FOR_ASSIGNMENTS = gql`
  query GetAssignedJobsForAssignments($input: AssignmentsQueryInput!) {
    jobsForAssignments(input: $input) {
      items {
        id
        category
        status
        scheduledDate
        startTime
        cleaningType
        notes
        isOwnerArrival
        isOwnerDeparture
        isMaintenanceStart
        isMaintenanceEnding
        isEarlyCheckin
        hasDateOverride
        hasTimeOverride
        isFlip
        isRecurringJob
        recurringJobId
        associatedCleaningJobStatus
        reservationId
        property {
          id
          name
          address
          propertyCode
          checkInTime
          checkOutTime
          area
          propertyManager {
            id
            firstName
            lastName
          }
          propertyManagementTeam {
            id
            name
          }
          propertyManagers {
            id
            firstName
            lastName
          }
        }
        reservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        previousReservation {
          id
          guest_name
          check_in
          check_out
          check_in_time
          check_out_time
          has_time_override
          override_check_in
          override_check_out
          type_name
          isOwnerBlock
          isMaintenanceBlock
          isNonPayingGuest
          isPayingGuest
        }
        assignedTo {
          id
          firstName
          lastName
          email
          name
          role
          phoneNumber
        }
        assignedTeamId
        assignedTeam {
          id
          name
          color
          memberCount
        }
        serviceType {
          id
          title
          category
          color
          timeBase
          defaultTime
        }
        customFlags {
          id
          flagType
          flagValue
          flagColor
          isManual
          addedByUserId
          sortOrder
          templateId
          template {
            id
            flagValue
            iconName
            backgroundColor
            textColor
            isBuiltIn
            isActive
          }
          createdAt
          updatedAt
        }
        createdAt
        updatedAt
      }
      assigned
      total
    }
  }
`;

// Timezone fix queries and mutations
export const PREVIEW_TIMEZONE_DATE_FIX = gql`
  query PreviewTimezoneDateFix {
    previewTimezoneDateFix {
      affectedJobs {
        id
        propertyName
        currentDate
        proposedDate
        assignedTo
        hasDateOverride
        updatedAt
      }
      totalCount
      message
    }
  }
`;

export const FIX_TIMEZONE_DATE_ISSUES = gql`
  mutation FixTimezoneDateIssues($jobIds: [String!]) {
    fixTimezoneDateIssues(jobIds: $jobIds) {
      success
      fixedCount
      errorCount
      errors
      message
    }
  }
`;

export const GET_JOB_EDIT_HISTORY = gql`
  query GetJobEditHistory($jobId: ID!) {
    jobEditHistory(jobId: $jobId) {
      id
      action
      source
      changes
      createdAt
      editedBy {
        id
        name
        firstName
        lastName
        email
      }
    }
  }
`;
